import type { FolderRole } from '@/lib/api/access'
import type { TemplateAccessEntry } from '@/types/domain'

/**
 * Who can reach a project, in a shape a person can read.
 *
 * `GET /v1/folders/:id/members` answers `{uid: role}` and nothing else — no
 * e-mail, no name. A dialog listing raw UUIDs is not a sharing dialog, so the
 * identities are borrowed from somewhere they *are* resolved:
 * `GET /v1/templates/:id/access` lists the template's folder-inherited
 * members with `email` and `name` filled in by the users service. One form in
 * the project is therefore enough to name everyone in it.
 *
 * It is a workaround and it has a hole: an **empty** project resolves nobody,
 * and those rows fall back to the uid. The fix belongs in the gateway —
 * `ListFolderMembers` should resolve identities the way `GetTemplateAccess`
 * already does. Reported.
 */

export interface MemberRow {
  uid: string
  role: FolderRole
  /** Display name, e-mail, or the uid when neither could be resolved. */
  label: string
  /** Second line, when the label is a name and the e-mail is also known. */
  secondary?: string
  /** True for the signed-in person, who is shown as "Sie" and never removed
   *  by this dialog — losing your own access from inside it leaves you
   *  looking at a project you can no longer open. */
  isSelf: boolean
  /** No identity could be resolved, so the row shows a uid. */
  unresolved: boolean
}

export const FOLDER_ROLE_LABELS: Record<FolderRole, string> = {
  viewer: 'Ansehen',
  contributor: 'Bearbeiten',
  manager: 'Verwalten',
}

export const FOLDER_ROLE_ORDER: FolderRole[] = ['viewer', 'contributor', 'manager']

/** uid → identity, from a template's access list. Only the entries the
 *  project itself contributed are used; a direct grant on the form says
 *  nothing about who is in the project. */
export function identitiesFromTemplateAccess(
  entries: TemplateAccessEntry[],
  folderId: string,
): Map<string, { email?: string; name?: string }> {
  const identities = new Map<string, { email?: string; name?: string }>()
  for (const entry of entries) {
    if (entry.source !== 'folder' || entry.folderId !== folderId) continue
    if (!entry.email && !entry.name) continue
    identities.set(entry.userId, { email: entry.email, name: entry.name })
  }
  return identities
}

export function memberRows(
  members: Record<string, FolderRole>,
  identities: Map<string, { email?: string; name?: string }>,
  selfUid: string | undefined,
): MemberRow[] {
  const rows = Object.entries(members).map<MemberRow>(([uid, role]) => {
    const identity = identities.get(uid)
    const name = identity?.name?.trim()
    const email = identity?.email?.trim()
    return {
      uid,
      role,
      label: name || email || uid,
      secondary: name && email ? email : undefined,
      isSelf: uid === selfUid,
      unresolved: !name && !email,
    }
  })

  // The signed-in person first, then resolved names alphabetically, then the
  // rows that are only a uid — those are the least useful and should not sit
  // at the top of the list.
  return rows.sort((a, b) => {
    if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1
    if (a.unresolved !== b.unresolved) return a.unresolved ? 1 : -1
    return a.label.localeCompare(b.label, 'de', { sensitivity: 'base' })
  })
}
